import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useGateValue } from '@statsig/react-bindings'
import Text from 'core-system/Text'
import Button from 'core-system/Button'
import palette from 'core-system/Themes/palette'
import kingCountyService, {
  BikeWaitlistStatusValue,
} from 'redux/KingCounty/kingCountyService'
import { BIKE_WAITLIST_SECTION_ID } from './BikeCatalogInfoBanner'

const Section = styled.div`
  margin-top: 1.5rem;
  scroll-margin-top: 1rem;
`

const Card = styled.div`
  ${(props) => props.theme.baseCard};
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const Meta = styled(Text)`
  font-size: 0.75rem;
  color: ${palette.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.25rem;
`

const ShortTermLeaseWaitlistSection = React.memo(() => {
  const isKingCounty = useGateValue('king_county')
  const [status, setStatus] = useState<BikeWaitlistStatusValue>(null)
  const [isLoadingStatus, setIsLoadingStatus] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isKingCounty) {
      return
    }

    let cancelled = false

    const loadStatus = async () => {
      setIsLoadingStatus(true)
      setError(null)
      try {
        const response = await kingCountyService.getBikeWaitlist()
        if (!cancelled) {
          setStatus(response.data?.status ?? null)
        }
      } catch {
        if (!cancelled) {
          setError('Unable to load waitlist status. Please try again.')
        }
      } finally {
        if (!cancelled) {
          setIsLoadingStatus(false)
        }
      }
    }

    loadStatus()
    return () => {
      cancelled = true
    }
  }, [isKingCounty])

  const handleJoinWaitlist = useCallback(async () => {
    setIsSubmitting(true)
    setError(null)
    try {
      const response = await kingCountyService.joinBikeWaitlist()
      setStatus(response.data?.status ?? null)
    } catch {
      setError('Unable to join the waitlist. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }, [])

  const handleWithdrawFromWaitlist = useCallback(async () => {
    setIsSubmitting(true)
    setError(null)
    try {
      const response = await kingCountyService.withdrawFromBikeWaitlist()
      setStatus(response.data?.status ?? null)
    } catch {
      setError('Unable to leave the waitlist. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }, [])

  const renderActions = () => {
    if (status === 'SELECTED') {
      return (
        <Text variant='action3' textColor={palette.secondary.purple700}>
          Congratulations! You were selected. Rybit has your information —
          head over to pick up your short-term lease bike.
        </Text>
      )
    }

    if (status === 'WAITING') {
      return (
        <>
          <Text variant='action3' textColor={palette.secondary.purple700}>
            You&apos;re on the waitlist.
          </Text>
          <Button
            variant='secondary'
            onClick={handleWithdrawFromWaitlist}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Leaving...' : 'Leave waitlist'}
          </Button>
        </>
      )
    }

    return (
      <Button
        onClick={handleJoinWaitlist}
        disabled={isLoadingStatus || isSubmitting}
      >
        {isSubmitting ? 'Joining...' : 'Join waitlist'}
      </Button>
    )
  }

  if (!isKingCounty) {
    return null
  }

  return (
    <Section id={BIKE_WAITLIST_SECTION_ID}>
      <Card>
        <Meta variant='action4'>Rybit</Meta>
        <Text variant='h4'>Short-term lease</Text>
        <Text variant='body2' textColor={palette.text.secondary}>
          King County is offering a limited number of short-term bike leases
          managed by Rybit. Join the waitlist and we&apos;ll connect you when a
          bike becomes available.
        </Text>
        <Actions>
          {renderActions()}
          {error ? (
            <Text variant='body2' textColor={palette.error.red500}>
              {error}
            </Text>
          ) : null}
        </Actions>
      </Card>
    </Section>
  )
})

if (process.env.NODE_ENV !== 'production') {
  ShortTermLeaseWaitlistSection.displayName = 'ShortTermLeaseWaitlistSection'
}

export default ShortTermLeaseWaitlistSection
